'use client';

import React, { useState } from "react";
import PropTypes from "prop-types";
import { FiEye, FiEyeOff } from "react-icons/fi";
import GradientWrapper from "../UI/GradientWrapper";

const TextInput = React.forwardRef(({
  label = "Input",
  name,
  value = "",
  type = "text",
  placeholder = "",
  className = "",
  error = "",
  inputClassName = "",
  labelClassName = "",
  variant = "default", // default | gradient
  rightElement = null,
  showCharCount = false,
  maxLength = null,
  onChange,
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const [charCount, setCharCount] = useState(value ? value.toString().length : 0);

  const handleChange = (e) => {
    setCharCount(e.target.value.length);
    if (onChange) onChange(e);
  };

  if (variant === "gradient") {
      return (
        <div className={`w-full ${className}`}>
             {label && (
                <label 
                    htmlFor={name} 
                    className={`block text-sm text-gray-400 mb-2 ${labelClassName}`}
                >
                    {label}
                </label>
             )}
            <GradientWrapper>
                <div className="relative w-full h-full">
                        <input
                            {...props}
                            id={name}
                            name={name}
                            ref={ref}
                            type={type === "password" && showPassword ? "text" : type}
                            placeholder={placeholder}
                            value={value}
                            onChange={handleChange}
                            maxLength={maxLength}
                            className={`w-full px-3 py-2 bg-transparent border-none text-white focus:outline-none h-10 ${inputClassName}`}
                        />
                    {type === "password" && (
                        <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 z-10 text-gray-400 hover:text-white focus:outline-none cursor-pointer"
                        >
                        {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                        </button>
                    )}
                </div>
            </GradientWrapper>
            <div className={`flex ${showCharCount && maxLength ? "justify-between" : "justify-end"} items-start mt-1`}>
                 {showCharCount && maxLength && (
                    <span className="text-gray-500 text-[10px] font-thin ml-auto">
                        {charCount}/{maxLength}
                    </span>
                 )}
            </div>
        </div>
      );
  }

  return (
    <div className={`relative w-full ${className}`}>
      <input
        {...props}
        id={name}
        name={name}
        ref={ref}
        type={type === "password" && showPassword ? "text" : type}
        placeholder={placeholder || " "}
        value={value}
        onChange={handleChange}
        maxLength={maxLength}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
        className={`peer w-full bg-color-98 text-color-1 text-sm border rounded-md py-2 
          transition duration-300 ease focus:outline-none 
          focus:border-[var(--accent-dark)] hover:border-[var(--accent-dark)] shadow-sm focus:shadow 
          ${type === "password" && rightElement ? "px-3 pr-20" : (type === "password" || rightElement ? "px-3 pr-10" : "px-3")}
          ${error ? "border-red-500" : "border-color-50"} ${inputClassName}`}
      />
      <label
        htmlFor={name}
        className={`absolute -top-2 left-2.5 bg-color-98 px-1 text-xs text-color-1 w-fit ${
          error ? "text-red-500" : ""
        } ${labelClassName}`}
      >
        {label}
      </label>
      
      {(type === "password" || rightElement) && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2 z-10">
            {rightElement}
            {type === "password" && (
                <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-color-1 hover:text-[var(--accent-light)] focus:text-[var(--accent-light)] focus:outline-none cursor-pointer"
                aria-label={showPassword ? "Hide password" : "Show password"}
                >
                {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
            )}
        </div>
      )}
      
      <div className={`flex ${showCharCount && maxLength ? "justify-between" : "justify-end"} items-start mt-1 px-1`}>
        {showCharCount && maxLength && (
            <span className="text-color-1 text-[10px] font-thin ml-auto">
                {charCount}/{maxLength}
            </span>
        )}
      </div>
    </div>
  );
});

TextInput.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  error: PropTypes.string,
  onChange: PropTypes.func,
  rightElement: PropTypes.node,
  variant: PropTypes.oneOf(['default', 'gradient']),
  showCharCount: PropTypes.bool,
  maxLength: PropTypes.number
};

export default TextInput;
